import { QuizQuestion } from "./QuizInline";

export type ParsedQuiz = {
    intro: string;          // text before the first "Question N :" (raw HTML kept)
    questions: QuizQuestion[];
};

/**
 * Full parser — returns the intro text AND the questions.
 * intro keeps original HTML so the chat bubble can render it via <Latex>.
 */
export function parseQuiz(rawText: string): ParsedQuiz {
    const empty: ParsedQuiz = { intro: "", questions: [] };
    try {
        // ── 0. Capture intro (raw HTML, before first Question marker) ───────────
        const introMatch = rawText.match(/^([\s\S]*?)(?=<[^>]*>\s*)?Question\s+\d+\s*:/i);
        const intro = introMatch ? introMatch[1].trim() : "";

        // ── 1. Strip HTML tags, decode basic entities (for parsing only) ────────
        const plain = rawText
            .replace(/<br\s*\/?>/gi, "\n")
            .replace(/<[^>]*>/g, "")
            .replace(/&amp;/g, "&")
            .replace(/&lt;/g, "<")
            .replace(/&gt;/g, ">")
            .replace(/&nbsp;/g, " ");

        // ── 2. Split into question blocks ──────────────────────────────────────
        const blocks = plain
            .split(/Question\s+\d+\s*:/i)
            .map((b) => b.trim())
            .filter((b) => b.length > 0);

        if (blocks.length === 0) return { intro, questions: [] };

        const questions: QuizQuestion[] = [];

        for (const block of blocks) {
            // ── 3. Extract answer letter (Réponse / Reponse) ────────────────────
            const answerMatch = block.match(/R[ée]ponse\s*:\s*([a-d])\s*\)/i);
            if (!answerMatch) continue;

            const answerLetter = answerMatch[1].toLowerCase();
            const answerIndex = answerLetter.charCodeAt(0) - "a".charCodeAt(0);

            // ── 4. Extract question text (before options / br) ──────────────────
            const questionMatch = block.match(
                /^([\s\S]+?)(?:\n\s*(?:Options?\s*:)?\s*\n?\s*a\s*\))/i
            );
            const questionText = questionMatch
                ? questionMatch[1].replace(/R[ée]ponse\s*:.*$/im, "").trim()
                : block.split(/\n/)[0].trim();

            if (!questionText) continue;

            // ── 5. Extract options a) b) c) d) ──────────────────────────────────
            const optionMatches = Array.from(
                block.matchAll(/([a-d])\s*\)\s*([\s\S]+?)(?=\n\s*[a-d]\s*\)|\nR[ée]|$)/gi)
            );

            if (optionMatches.length < 2) continue;

            const options = optionMatches.map((m) =>
                m[2].replace(/R[ée]ponse\s*:.*$/im, "").replace(/\n/g, " ").trim()
            );

            if (answerIndex < 0 || answerIndex >= options.length) continue;

            questions.push({ q: questionText, options, answer: answerIndex });
        }

        return { intro, questions };
    } catch (_) {
        return empty;
    }
}

/**
 * Backwards-compatible wrapper — returns only the questions array.
 */
export function parseQuizFromText(rawText: string): QuizQuestion[] {
    return parseQuiz(rawText).questions;
}